import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  Edit, 
  Eye, 
  Plus,
  Calendar,
  Clock,
  User,
  MapPin,
  AlertCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Trash2,
  FileText,
  Mail,
  PenLine,
  MinusCircle,
} from 'lucide-react';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { ShopColumnHeader, ShopColumnCell } from '../components/ShopListColumn';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { ListPaginationFooter } from '../components/ListPaginationFooter';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useToast } from '../components/Toast';
import { RootState } from '../store';
import { summarizeAdminAppointmentPricing, pricingSummaryToTooltipLines } from '../utils/adminAppointmentPricing';
import { AppointmentQuickFind } from '../components/reception/AppointmentQuickFind';
import { AppointmentStatusBadge } from '../components/appointment/AppointmentStatusBadge';
import {
  APPOINTMENT_STATUS_OPTIONS,
  appointmentStatusLabel,
} from '../components/appointment/appointmentConsultationUtils';
import { DateInput } from '../components/DateInput';

interface Appointment {
  id: string;
  actId?: string;
  contractId?: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  practitionerFirstName: string | null;
  practitionerLastName: string | null;
  shopName: string;
  serviceName: string;
  scheduledAt: string;
  estimatedEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  clientIsMinor?: boolean | null;
  bookingSource?: 'ONLINE' | 'BACKOFFICE' | 'WALK_IN';
  status: 'SCHEDULED' | 'CONFIRMED' | 'CHECKED_IN' | 'IN_PROGRESS' | 'AWAITING_CHECKOUT' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW' | 'RESCHEDULED';
  type: 'CONSULTATION' | 'TATTOO_SESSION' | 'PIERCING' | 'TOUCH_UP' | 'REMOVAL' | 'AFTERCARE';
  notes: string;
  clientNotes?: string;
  depositAmount?: number | null;
  pricing: {
    basePrice: number;
    piercingTypes?: Array<{ id?: string; name?: string; price?: number }>;
    piercingType?: { id?: string; name?: string; price?: number };
    workflowSupplements?: Array<{ id?: string; name?: string; price?: number; qty?: number }>;
    supplements?: Array<{ name: string; price: number }>;
    subtotal: number;
    deposit: number;
    total: number;
    paymentMethod: string;
    currency: string;
  };
  createdAt: string;
  updatedAt: string;
  signatureStatus?: 'none' | 'unsigned' | 'partial' | 'signed';
  signedRoles?: string[];
  requiredSigners?: string[];
  checkInCode?: string | null;
}

interface AppointmentsResponse {
  success: boolean;
  data: {
    appointments: Appointment[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

interface DocumentTemplate {
  id: string;
  code: string;
  version: number;
  language: string;
  isActive: boolean;
}


const dateTimeToLocalDateKey = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateOnlyFr = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]}`;
};

export function AppointmentsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';
  
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters and pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // Show all appointments by default
  const [typeFilter, setTypeFilter] = useState('');
  const [bookingSourceFilter, setBookingSourceFilter] = useState('');
  const [minorFilter, setMinorFilter] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [practitionerFilter, setPractitionerFilter] = useState('');
  const [sortKey, setSortKey] = useState('scheduledAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [generateModal, setGenerateModal] = useState<{
    open: boolean;
    appointment: Appointment | null;
    selectedTemplateId: string | null;
    loading: boolean;
    error: string | null;
  }>({
    open: false,
    appointment: null,
    selectedTemplateId: null,
    loading: false,
    error: null,
  });
  
  const [limit, setLimit] = useState(20);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    appointmentId: string | null;
    appointmentInfo: string;
  }>({
    isOpen: false,
    appointmentId: null,
    appointmentInfo: ''
  });

  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{
    isOpen: boolean;
    appointmentIds: string[];
  }>({
    isOpen: false,
    appointmentIds: [],
  });
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{
    isOpen: boolean;
    appointmentIds: string[];
  }>({
    isOpen: false,
    appointmentIds: [],
  });
  const [deleting, setDeleting] = useState(false);
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);
  const [resendingConfirmationId, setResendingConfirmationId] = useState<string | null>(null);
  const isBusy = loading || deleting || bulkHardDeleting || generateModal.loading;
  const fetchRequestIdRef = useRef(0);

  const visibleAppointmentIds = useMemo(() => appointments.map((a) => a.id), [appointments]);
  const {
    selectedIds: selectedAppointmentIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleAppointmentIds);
  const { needsShopSelection, canLoad, currentShopId, isAllShopsMode } = useShopScopedPage();

  useEffect(() => {
    if (!canLoad) return;
    fetchAppointments();
  }, [canLoad, currentShopId, search, statusFilter, typeFilter, bookingSourceFilter, minorFilter, fromDate, practitionerFilter, sortKey, sortDir, page, limit]);

  useEffect(() => {
    void fetchTemplates();
  }, []);

  const fetchAppointments = async () => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const baseParams = {
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
        ...(bookingSourceFilter && { bookingSource: bookingSourceFilter }),
        ...(minorFilter && { minor: 'true' }),
        ...(practitionerFilter && { practitionerId: practitionerFilter }),
      };

      const hasDateFilter = Boolean(fromDate);

      if (!hasDateFilter) {
        const params = new URLSearchParams({
          ...baseParams,
          page: page.toString(),
          limit: limit.toString(),
        });

        console.log('🔄 Fetching appointments with params:', params.toString());

        const response = await apiClient(`/api/admin/appointments?${params}`);
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          console.error('Appointments API error:', response.status, data);
          throw new Error(data?.message || `Failed to fetch appointments (${response.status})`);
        }

        if (!data?.success) {
          throw new Error(data?.message || 'Failed to load appointments');
        }

        if (requestId !== fetchRequestIdRef.current) return;

        setAppointments(data.data.appointments);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
        return;
      }

      // Date filtering is performed locally because the appointments endpoint
      // does not reliably accept/apply dateFrom/dateTo across deployments.
      // Keep all the other filters server-side, fetch all matching pages, then
      // filter scheduledAt by local calendar day and paginate the result here.
      const fetchLimit = 100;
      const firstParams = new URLSearchParams({
        ...baseParams,
        page: '1',
        limit: fetchLimit.toString(),
      });

      console.log('🔄 Fetching appointments for local date filtering:', firstParams.toString());

      const firstResponse = await apiClient(`/api/admin/appointments?${firstParams}`);
      const firstData: AppointmentsResponse | null = await firstResponse.json().catch(() => null);

      if (!firstResponse.ok || !firstData?.success) {
        console.error('Appointments API error:', firstResponse.status, firstData);
        throw new Error(firstData?.message || `Failed to fetch appointments (${firstResponse.status})`);
      }

      let allAppointments = [...firstData.data.appointments];
      const serverTotalPages = firstData.data.totalPages || 1;

      for (let serverPage = 2; serverPage <= serverTotalPages; serverPage += 1) {
        const params = new URLSearchParams({
          ...baseParams,
          page: serverPage.toString(),
          limit: fetchLimit.toString(),
        });

        const response = await apiClient(`/api/admin/appointments?${params}`);
        const data: AppointmentsResponse | null = await response.json().catch(() => null);

        if (!response.ok || !data?.success) {
          console.error('Appointments API error:', response.status, data);
          throw new Error(data?.message || `Failed to fetch appointments (${response.status})`);
        }

        allAppointments = allAppointments.concat(data.data.appointments);
      }

      const filteredAppointments = allAppointments.filter((appointment) => {
        const appointmentDate = dateTimeToLocalDateKey(appointment.scheduledAt);
        if (!appointmentDate) return false;
        if (fromDate && appointmentDate !== fromDate) return false;
        return true;
      });

      const filteredTotal = filteredAppointments.length;
      const filteredTotalPages = Math.max(1, Math.ceil(filteredTotal / limit));
      const safePage = Math.min(page, filteredTotalPages);
      const startIndex = (safePage - 1) * limit;
      const visibleAppointments = filteredAppointments.slice(startIndex, startIndex + limit);

      if (requestId !== fetchRequestIdRef.current) return;

      if (safePage !== page) {
        setPage(safePage);
      }

      setAppointments(visibleAppointments);
      setTotal(filteredTotal);
      setTotalPages(filteredTotalPages);
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error('Fetch appointments error:', error);
      setError(error instanceof Error ? error.message : 'Failed to load appointments');
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearchFilterChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleBookingSourceFilterChange = (value: string) => {
    setBookingSourceFilter(value);
    setPage(1);
  };

  const handleMinorFilterChange = (checked: boolean) => {
    setMinorFilter(checked);
    setPage(1);
  };

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    setPage(1);
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (appointmentId: string) => {
    navigate(`/appointments/${appointmentId}/edit`);
  };

  const handleView = (appointmentId: string) => {
    navigate(`/appointments/${appointmentId}`);
  };

  const handleCreate = () => {
    navigate('/appointments/create');
  };

  const handleResendConfirmationEmail = async (appointment: Appointment) => {
    try {
      setResendingConfirmationId(appointment.id);

      const response = await apiClient(
        `/api/admin/appointments/${appointment.id}/resend-confirmation-email`,
        { method: 'POST' },
      );

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const msg = data?.message || data?.error || 'Échec du renvoi de l’email de confirmation';
        showToast(msg, 'error', 7000);
        return;
      }

      showToast('Email de confirmation renvoyé avec succès', 'success');
    } catch (error) {
      console.error('Resend confirmation email error:', error);
      showToast('Échec du renvoi de l’email de confirmation', 'error', 7000);
    } finally {
      setResendingConfirmationId(null);
    }
  };

  const handleDelete = async (appointmentId: string, appointmentInfo: string) => {
    setDeleteConfirm({
      isOpen: true,
      appointmentId,
      appointmentInfo
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.appointmentId) return;
    
    try {
      setDeleting(true);
      
      console.log('🗑️ Deleting appointment:', deleteConfirm.appointmentId);
      
      const response = await apiClient(`/api/admin/appointments/${deleteConfirm.appointmentId}`, {
        method: 'DELETE',
      });

      console.log('📡 Delete response status:', response.status);

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const msg = data?.message || data?.error || 'Échec de la suppression du rendez-vous';
        setError(msg);
        showToast(msg, 'error', 7000);
        return;
      }
      console.log('📡 Delete response data:', data);
      
      console.log('✅ Delete successful, refreshing list...');
      // Close the modal first
      setDeleteConfirm({ isOpen: false, appointmentId: null, appointmentInfo: '' });
      // Then refresh the appointments list
      await fetchAppointments();
      console.log('🔄 List refreshed');
      setError(null); // Clear any previous errors
      showToast('Rendez-vous supprimé avec succès', 'success');
    } catch (error) {
      console.error('Delete appointment error:', error);
      setError('Failed to delete appointment');
      showToast('Échec de la suppression du rendez-vous', 'error', 7000);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ isOpen: false, appointmentId: null, appointmentInfo: '' });
  };

  const openBulkDeleteConfirm = () => {
    if (selectedAppointmentIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, appointmentIds: Array.from(selectedAppointmentIds) });
  };

  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, appointmentIds: [] });

  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.appointmentIds.length === 0) return;
    try {
      setDeleting(true);
      setError(null);

      const failed: Array<{ id: string; message: string }> = [];

      for (const appointmentId of bulkDeleteConfirm.appointmentIds) {
        try {
          const resp = await apiClient(`/api/admin/appointments/${appointmentId}`, { method: 'DELETE' });
          let data: any = null;
          try {
            data = await resp.json();
          } catch {
            failed.push({ id: appointmentId, message: 'Réponse du serveur invalide' });
            continue;
          }
          if (!resp.ok || !data?.success) {
            failed.push({ id: appointmentId, message: data?.message || data?.error || 'Échec de la suppression' });
          }
        } catch (e) {
          failed.push({ id: appointmentId, message: 'Une erreur est survenue lors de la suppression.' });
        }
      }

      setBulkDeleteConfirm({ isOpen: false, appointmentIds: [] });
      clearSelection();
      await fetchAppointments();

      const deletedCount = bulkDeleteConfirm.appointmentIds.length - failed.length;
      if (failed.length === 0) {
        showToast(
          `${deletedCount} rendez-vous supprimé${deletedCount !== 1 ? 's' : ''} avec succès`,
          'success',
        );
      } else if (deletedCount === 0) {
        const msg = `Aucun rendez-vous supprimé. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      } else {
        const msg = `${deletedCount} rendez-vous supprimé${deletedCount !== 1 ? 's' : ''}. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      }
    } finally {
      setDeleting(false);
    }
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedAppointmentIds.size === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, appointmentIds: Array.from(selectedAppointmentIds) });
  };

  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, appointmentIds: [] });

  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.appointmentIds.length === 0) return;
    try {
      setBulkHardDeleting(true);
      setError(null);

      const failed: Array<{ id: string; message: string }> = [];

      for (const appointmentId of bulkHardDeleteConfirm.appointmentIds) {
        try {
          const resp = await apiClient(`/api/admin/appointments/${appointmentId}?hardDelete=true`, { method: 'DELETE' });
          let data: any = null;
          try {
            data = await resp.json();
          } catch {
            failed.push({ id: appointmentId, message: 'Réponse du serveur invalide' });
            continue;
          }
          if (!resp.ok || !data?.success) {
            failed.push({ id: appointmentId, message: data?.message || data?.error || 'Échec de la suppression définitive' });
          }
        } catch (e) {
          failed.push({ id: appointmentId, message: 'Une erreur est survenue lors de la suppression définitive.' });
        }
      }

      setBulkHardDeleteConfirm({ isOpen: false, appointmentIds: [] });
      clearSelection();
      await fetchAppointments();

      const deletedCount = bulkHardDeleteConfirm.appointmentIds.length - failed.length;
      if (failed.length === 0) {
        showToast(
          `${deletedCount} rendez-vous supprimé${deletedCount !== 1 ? 's' : ''} définitivement`,
          'success',
        );
      } else if (deletedCount === 0) {
        const msg = `Aucun rendez-vous supprimé définitivement. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      } else {
        const msg = `${deletedCount} rendez-vous supprimé${deletedCount !== 1 ? 's' : ''} définitivement. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`;
        setError(msg);
        showToast(msg, 'error', 7000);
      }
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await apiClient('/api/document-templates');
      if (!res.ok) throw new Error('Failed to load templates');
      const data = await res.json();
      const active = (data || []).filter((t: DocumentTemplate) => t.isActive);
      setTemplates(active);
    } catch (err) {
      console.error('Failed to load templates', err);
    }
  };

  const openGenerateModal = (appointment: Appointment) => {
    const defaultTemplate = templates[0]?.id ?? null;
    setGenerateModal({
      open: true,
      appointment,
      selectedTemplateId: defaultTemplate,
      loading: false,
      error: null,
    });
  };

  const closeGenerateModal = () => {
    setGenerateModal({
      open: false,
      appointment: null,
      selectedTemplateId: null,
      loading: false,
      error: null,
    });
  };

  const handleGenerate = async () => {
    if (!generateModal.appointment) return;
    const template = templates.find((t) => t.id === generateModal.selectedTemplateId);
    if (!template) {
      setGenerateModal((prev) => ({ ...prev, error: 'Sélectionnez un modèle actif.' }));
      return;
    }

    try {
      setGenerateModal((prev) => ({ ...prev, loading: true, error: null }));
      let contractId = generateModal.appointment.contractId;
      const actId = generateModal.appointment.actId;

      if (!actId) {
        throw new Error('Aucun acte associé à ce rendez-vous. Veuillez d\'abord créer un acte.');
      }

      // Auto-create contract for the act if missing
      if (!contractId) {
        const createRes = await apiClient(`/api/admin/acts/${actId}/contract`, {
          method: 'POST',
          body: JSON.stringify({
            type: 'TATTOO_CONSENT',
            templateVersion: 'v1',
          }),
        });
        if (!createRes.ok) {
          const text = await createRes.text();
          throw new Error(text || 'Échec de la création du contrat');
        }
        const createData = await createRes.json();
        contractId = createData?.data?.id || createData?.id;
        if (!contractId) {
          throw new Error('Contrat créé mais ID introuvable');
        }
        setAppointments((prev) =>
          prev.map((a) => (a.id === generateModal.appointment?.id ? { ...a, contractId } : a)),
        );
      }

      const res = await apiClient('/api/documents/generate', {
        method: 'POST',
        body: JSON.stringify({
          templateCode: template.code,
          entityType: 'CONTRACT',
          entityId: contractId,
          language: template.language,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Échec de la génération du document');
      }
      await res.json(); // ignore body, success assumed
      showToast('Document généré avec succès', 'success');
      closeGenerateModal();
    } catch (err: any) {
      setGenerateModal((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Échec de la génération du document',
      }));
    }
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      CONSULTATION: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Consultation' },
      TATTOO_SESSION: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Tatouage' },
      PIERCING: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Piercing' },
      TOUCH_UP: { bg: 'bg-green-100', text: 'text-green-800', label: 'Retouche' },
      REMOVAL: { bg: 'bg-red-100', text: 'text-red-800', label: 'Détatouage' },
      AFTERCARE: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Suivi' }
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.CONSULTATION;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getBookingSourceBadge = (source?: Appointment['bookingSource']) => {
    const sourceConfig = {
      ONLINE: { label: 'En ligne', classes: 'border-blue-200 bg-blue-100 text-blue-700' },
      BACKOFFICE: { label: 'Backoffice', classes: 'border-primary-200 bg-primary-100 text-primary-700' },
      WALK_IN: { label: 'Sans RDV', classes: 'border-emerald-200 bg-emerald-100 text-emerald-700' },
    };
    const config = source ? sourceConfig[source] : undefined;

    return config ? (
      <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${config.classes}`}>
        {config.label}
      </span>
    ) : (
      <span className="text-sm text-gray-400">—</span>
    );
  };

  const roleLabel: Record<string, string> = {
    CLIENT: 'Client',
    LEGAL_GUARDIAN: 'Tuteur',
    PRACTITIONER: 'Praticien',
    SHOP_MANAGER: 'Responsable',
  };

  const getSignatureBadge = (appointment: Appointment) => {
    const status = appointment.signatureStatus;
    const required = appointment.requiredSigners ?? [];
    const signed = appointment.signedRoles ?? [];

    if (!status || status === 'none') {
      return null; // no document generated — show nothing
    }

    if (status === 'signed') {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
          title={`Signé par : ${signed.map((r) => roleLabel[r] ?? r).join(', ')}`}
        >
          <CheckCircle className="w-3 h-3" />
          Signé
        </span>
      );
    }

    if (status === 'partial') {
      const missing = required.filter((r) => !signed.includes(r));
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
          title={`Signé : ${signed.map((r) => roleLabel[r] ?? r).join(', ')} — En attente : ${missing.map((r) => roleLabel[r] ?? r).join(', ')}`}
        >
          <PenLine className="w-3 h-3" />
          Partiel ({signed.length}/{required.length})
        </span>
      );
    }

    // unsigned
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"
        title={`En attente : ${required.map((r) => roleLabel[r] ?? r).join(', ')}`}
      >
        <MinusCircle className="w-3 h-3" />
        Non signé
      </span>
    );
  };

  // Removed unused formatTimeRemaining function

  const formatDateTime = (dateString: string | null) => {
    // Handle null, undefined, or invalid date values
    if (!dateString || dateString === null) {
      return {
        date: 'Non définie',
        time: '--:--'
      };
    }
    
    try {
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return {
          date: 'Date invalide',
          time: '--:--'
        };
      }
      
      return {
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        time: date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      };
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return {
        date: 'Erreur de date',
        time: '--:--'
      };
    }
  };

  const formatMoney = (value: number) => `${Math.round(value * 100) / 100}€`;

  const getPricingBreakdown = (appointment: Appointment) => {
    const summary = summarizeAdminAppointmentPricing(
      appointment.pricing as unknown as Record<string, unknown>,
      {
        appointmentType: appointment.type,
        recordedDepositAmount: appointment.depositAmount,
      },
    );
    const lines = pricingSummaryToTooltipLines(summary, formatMoney);
    return {
      total: summary.displayTotal,
      base: summary.basePrice,
      deposit: summary.deposit,
      lines,
    };
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  if (loading && appointments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des rendez-vous...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      {/* Toast Notifications */}
      <ToastComponent />
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Gestion des Rendez-vous</span>
              <HelpIcon 
                docPath="07-gestion-rendez-vous#07-2-liste" 
                tooltip="Documentation : Liste des rendez-vous"
              />
            </h1>
            <p className="text-textSecondary mt-1">
              {total} rendez-vous au total
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Nouveau Rendez-vous</span>
          </button>
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={openBulkDeleteConfirm}
                disabled={deleting || bulkHardDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer les rendez-vous sélectionnés"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
                <button
                  onClick={openBulkHardDeleteConfirm}
                  disabled={deleting || bulkHardDeleting}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Supprimer définitivement les rendez-vous sélectionnés"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Suppression définitive ({selectedCount})</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          {/* Search */}
          <div className="relative md:col-span-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Client, email ou code d’accueil…"
              value={search}
              onChange={(e) => handleSearchFilterChange(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          <div className="md:col-span-3">
            <AppointmentQuickFind onError={(message) => showToast(message, 'error')} />
          </div>

          {/* Status Filter */}
          <div className="relative md:col-span-2">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les statuts</option>
              {APPOINTMENT_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {appointmentStatusLabel(status)}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Type Filter */}
          <div className="relative md:col-span-2">
            <select
              value={typeFilter}
              onChange={(e) => handleTypeFilterChange(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les types</option>
              <option value="CONSULTATION">Consultation</option>
              <option value="TATTOO_SESSION">Tatouage</option>
              <option value="PIERCING">Piercing</option>
              <option value="TOUCH_UP">Retouche</option>
              <option value="REMOVAL">Détatouage</option>
              <option value="AFTERCARE">Suivi</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Booking source filter */}
          <div className="relative md:col-span-2">
            <select
              value={bookingSourceFilter}
              onChange={(e) => handleBookingSourceFilterChange(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les sources</option>
              <option value="ONLINE">En ligne</option>
              <option value="BACKOFFICE">Backoffice</option>
              <option value="WALK_IN">Sans RDV</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Minor filter */}
          <label className="flex min-h-[42px] items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={minorFilter}
              onChange={(e) => handleMinorFilterChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Mineurs uniquement</span>
          </label>

          {/* Date Filter */}
          <div className="relative md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Date
            </label>
            <DateInput
              value={fromDate}
              onChange={handleFromDateChange}
              className="input-field"
            />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch('');
              setStatusFilter('');
              setTypeFilter('');
              setBookingSourceFilter('');
              setMinorFilter(false);
              setFromDate('');
              setPractitionerFilter('');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2 md:col-span-1"
          >
            <Filter className="w-4 h-4" />
            <span className="md:hidden">Effacer</span>
          </button>
        </div>
        
        {/* Active Date Indicator */}
        {fromDate && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
              <Calendar className="w-4 h-4 mr-2" />
              <span className="font-medium">Date:</span>
              <span className="ml-2">{formatDateOnlyFr(fromDate)}</span>
            </span>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchAppointments}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Appointments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={appointments.length === 0 || deleting || bulkHardDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Sélectionner tous les rendez-vous de la page"
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    {sortKey === 'type' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('scheduledAt')}
                >
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Date & Heure</span>
                    {sortKey === 'scheduledAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'status' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>Créé le</span>
                    {sortKey === 'createdAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('clientLastName')}
                >
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>Client</span>
                    {sortKey === 'clientLastName' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('serviceName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Service</span>
                    {sortKey === 'serviceName' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('practitionerLastName')}
                >
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{isAllShopsMode ? t('navigation.practitioners.assigned', 'Praticien') : `${t('navigation.practitioners.assigned')} & Shop`}</span>
                    {sortKey === 'practitionerLastName' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <ShopColumnHeader visible={isAllShopsMode} />
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('price')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Prix</span>
                    {sortKey === 'price' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <PenLine className="w-4 h-4" />
                    <span>Signatures</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {appointments.map((appointment) => {
                const dateTime = formatDateTime(appointment.scheduledAt);
                return (
                  <tr key={appointment.id} className="group hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedAppointmentIds.has(appointment.id)}
                        disabled={deleting || bulkHardDeleting}
                        onChange={() => toggleSelect(appointment.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        aria-label={`Sélectionner le rendez-vous ${appointment.clientFirstName} ${appointment.clientLastName}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleView(appointment.id)}
                        className="hover:opacity-80 transition-opacity"
                        title="Voir le rendez-vous"
                      >
                        {getTypeBadge(appointment.type)}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getBookingSourceBadge(appointment.bookingSource)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {dateTime.date}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {dateTime.time}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <AppointmentStatusBadge status={appointment.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const createdDateTime = formatDateTime(appointment.createdAt);
                        return (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {createdDateTime.date}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {createdDateTime.time}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {appointment.clientFirstName}{' '}
                          {appointment.clientLastName}
                        </span>

                        {appointment.clientIsMinor && (
                          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                            Mineur
                          </span>
                        )}
                      </div>

                      <div className="mt-0.5 text-sm text-gray-500">
                        {appointment.clientEmail}
                      </div>
                    </div>
                  </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {appointment.checkInCode ? (
                        <span className="text-sm font-semibold tracking-[0.2em] text-gray-900">
                          {appointment.checkInCode}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {appointment.serviceName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.practitionerFirstName && appointment.practitionerLastName
                            ? `${appointment.practitionerFirstName} ${appointment.practitionerLastName}`
                            : 'Non assigné'}
                        </div>
                        {!isAllShopsMode && (
                          <div className="text-sm text-gray-500">{appointment.shopName}</div>
                        )}
                      </div>
                    </td>
                    <ShopColumnCell visible={isAllShopsMode} shopName={appointment.shopName} />
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const pricingBreakdown = getPricingBreakdown(appointment);
                        return (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatMoney(pricingBreakdown.total)}
                            </div>
                            <div className="text-xs text-gray-500">
                              Base: {formatMoney(pricingBreakdown.base)}
                            </div>
                            {pricingBreakdown.lines.map((line, index) => (
                              <div key={`${appointment.id}-price-line-${index}`} className="text-xs text-gray-500">
                                {line}
                              </div>
                            ))}
                            {pricingBreakdown.deposit > 0 && (
                              <div className="text-xs text-gray-500">
                                Acompte: {formatMoney(pricingBreakdown.deposit)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSignatureBadge(appointment)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleView(appointment.id)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded"
                          title="Voir le rendez-vous"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openGenerateModal(appointment)}
                          className="text-indigo-600 hover:text-indigo-700 p-1 rounded"
                          title="Générer un document"
                          disabled={templates.length === 0}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => void handleResendConfirmationEmail(appointment)}
                          className="text-blue-600 hover:text-blue-700 p-1 rounded disabled:opacity-50"
                          title="Renvoyer l’email de confirmation"
                          disabled={resendingConfirmationId === appointment.id}
                        >
                          {resendingConfirmationId === appointment.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(appointment.id)}
                          className="text-primary-600 hover:text-primary-700 p-1 rounded"
                          title="Modifier le rendez-vous"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(appointment.id, `${appointment.clientFirstName} ${appointment.clientLastName} - ${new Date(appointment.scheduledAt).toLocaleDateString()}`)}
                          className="text-red-600 hover:text-red-700 p-1 rounded"
                          title="Supprimer le rendez-vous"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Empty State */}
        {!loading && appointments.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun rendez-vous trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || statusFilter || typeFilter || fromDate 
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Commencez par créer votre premier rendez-vous.'
              }
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Nouveau Rendez-vous</span>
              </button>
            </div>
          </div>
        )}

        <ListPaginationFooter
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          disabled={isBusy}
          onPageChange={setPage}
          onLimitChange={(next) => {
            setLimit(next);
            setPage(1);
          }}
        />
      </div>

      {/* Loading overlay for subsequent loads */}
      {loading && appointments.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.isOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${deleting ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer le rendez-vous de <strong>{deleteConfirm.appointmentInfo}</strong> ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${deleting ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.appointmentIds.length}</strong> rendez-vous ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={deleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Hard Delete: two-step confirmation */}
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel="rendez-vous"
        count={bulkHardDeleteConfirm.appointmentIds.length}
        isLoading={bulkHardDeleting}
      />

      {/* Generate Document Modal */}
      {generateModal.open && generateModal.appointment && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${generateModal.loading ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">Générer un document</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {generateModal.appointment.clientFirstName} {generateModal.appointment.clientLastName} —{' '}
                  {new Date(generateModal.appointment.scheduledAt).toLocaleString('fr-FR')}
                </p>
              </div>
              <button onClick={closeGenerateModal} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Modèle
                <select
                  className="mt-1 w-full border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={generateModal.selectedTemplateId ?? ''}
                  onChange={(e) =>
                    setGenerateModal((prev) => ({ ...prev, selectedTemplateId: e.target.value }))
                  }
                >
                  {templates.length === 0 && <option value="">Aucun modèle actif</option>}
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.code} · v{tpl.version} · {tpl.language}
                    </option>
                  ))}
                </select>
              </label>

              {generateModal.error && (
                <div className="text-sm text-red-600">{generateModal.error}</div>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={closeGenerateModal}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={() => void handleGenerate()}
                disabled={generateModal.loading || templates.length === 0}
                className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {generateModal.loading ? 'Génération...' : 'Générer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}